import { useManager } from 'dreamstate';
import { ReactNode, useEffect } from 'react';

import { rem, Box, Flex } from '@vimeo/bokeh';
import { ReviewQuestion } from '@vimeo/bokeh-icons';
import { BlockingLoadingWrapper } from '@vimeo/live-core/components/loading';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import { HorizontalScrollShadow } from '@vimeo/live-core/components/scroll_shadow';
import { InteractionFirebaseManager } from '@vimeo/live-core/store/firebase/base';
import { QnAManager, useQnaQuestions } from '@vimeo/live-core/store/firebase/interaction/qna';
import { trackViewQna } from '@vimeo/live-core/tracking/tracking_engagement_qna';
import { trackVenuesViewQa } from '@vimeo/live-core/tracking/tracking_venues_engagement';
import { EComposerSessionType } from '@vimeo/live-core/types/composer_session';
import { IQnAQuestion } from '@vimeo/live-core/types/interaction_tools';
import { EQnaTab } from '@vimeo/live-core/types/interaction_tools';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';

import { createInteractionDomName } from '../../../../libs/shared/utils/dom/identifying';
import { EmptyStatePlaceholder } from '../../../shared/tools/shared';
import { TAB_LIST_COLUMN_ROW_ITEM_STYLE } from '../../../shared/tools/styles';
import { QnaInputForm } from '../../qna/form/QnaInputForm';
import { NewQuestionViewItem } from '../../qna/question_item/NewQuestionViewItem';
import { QuestionsList } from '../../qna/questions_list/QuestionsList';

export function QnaView({
  id = createInteractionDomName('qna-view'),
  // eslint-disable-next-line react-hooks/rules-of-hooks
  firebaseContext: { connectionStatuses: { isInteractionConnected } } = useManager(
    InteractionFirebaseManager,
  ),
  qnaContext: {
    activeSessionId,
    activeSession,
    activeSessionQuestions,
    activeSessionPendingQuestions,
    activeSessionPinnedQuestionId,
    isHydrated,
    qnaReplies,
    qnaActions: { voteQuestion },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(QnAManager),
}) {
  const {
    initialState,
    initialState: { sessionType },
  } = useLiveGlobals<IInteractionStoreInitialState>();
  const { activeQuestions } = useQnaQuestions(
    activeSessionPendingQuestions,
    activeSessionQuestions,
  );

  useEffect(() => {
    trackViewQna();
  }, []);

  useEffect(() => {
    if (isHydrated && activeSessionId && sessionType === EComposerSessionType.VENUE) {
      trackVenuesViewQa(String(activeSessionId));
    }
  }, [activeSessionId, isHydrated, sessionType]);

  if (!isHydrated) {
    return (
      <Flex
        id={id}
        justifyContent={'center'}
        alignItems={'center'}
        height={'100%'}
        textAlign={'center'}
      >
        <BlockingLoadingWrapper
          id={createInteractionDomName(id, 'loader')}
          opacity={0.1}
          withAnimation={true}
        />
      </Flex>
    );
  }

  if (activeSession) {
    return (
      <Flex
        id={id}
        position={'relative'}
        direction={'column'}
        grow={1}
        maxHeight={'100%'}
        overflowY={'hidden'}
        padding={`${rem(24)} ${rem(24)} 0 ${rem(24)}`}
      >
        <Flex
          id={createInteractionDomName(id, 'questions-panel')}
          direction={'column'}
          grow={1}
          overflow={'hidden'}
          rowGap={rem(16)}
        >
          <QuestionsList
            id={createInteractionDomName(id, 'questions-list')}
            tabId={EQnaTab.ACTIVE}
            questions={activeQuestions}
            fullWidth={true}
            pinnedQuestionId={activeSessionPinnedQuestionId}
            scrollBackground={initialState?.interaction?.backgroundColor || 'background'}
            itemRenderer={(question: IQnAQuestion): ReactNode => (
              <Box key={question.id} sx={TAB_LIST_COLUMN_ROW_ITEM_STYLE}>
                <NewQuestionViewItem
                  question={question}
                  isPinned={question.id === activeSessionPinnedQuestionId}
                  hasVoteButton={true}
                  questionReplies={qnaReplies[question.id]}
                  onQuestionVoted={voteQuestion}
                />
              </Box>
            )}
            placeholder={
              <EmptyStatePlaceholder
                id={createInteractionDomName(id, 'questions-list-placeholder')}
                icon={<ReviewQuestion boxSize={'lg'} />}
                control={null}
              />
            }
          />
        </Flex>

        <Box
          id={createInteractionDomName(id, 'input-wrapper')}
          position={'relative'}
          width={'100%'}
          paddingBottom={rem(16)}
        >
          <HorizontalScrollShadow
            color={initialState?.interaction?.backgroundColor || 'background'}
          />
          <QnaInputForm
            id={createInteractionDomName(id, 'input-form')}
            isDisabled={!isInteractionConnected}
          />
        </Box>
      </Flex>
    );
  } else {
    return (
      <Flex
        id={id}
        justifyContent={'center'}
        alignItems={'center'}
        height={'100%'}
        textAlign={'center'}
      >
        <EmptyStatePlaceholder
          id={createInteractionDomName(id, 'questions-list-placeholder')}
          icon={<ReviewQuestion boxSize={'lg'} />}
          control={null}
        />
      </Flex>
    );
  }
}
